import React, { useState } from 'react'
import { css } from '@emotion/core'
import {
  DangerButton,
  Button,
  List,
  ListItem,
  InlineButton,
  DialogOverlay,
  DialogOverlayFooter,
  DialogOverlayActions,
  Paragraph,
} from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { PersonRemove, MenuNewWindow, DocumentBasic } from '@fs/zion-icon'
import { getNestedOrFloatingElementById, getPersonName, openRelationshipArk, useSlsStateContext } from '@fs/zion-sls'

export default function CrossRecordWarningOverlay({
  overlay,
  onCancel,
  onContinue,
  person,
  existingRelationships,
  genericVersion,
}) {
  const [t] = useTranslation()
  const state = useSlsStateContext()
  const [viewAll, setViewAll] = useState(false)
  const name = getPersonName({ person })

  const relationshipsListCss = css`
    max-height: 140px;
    overflow-x: hidden;
    overflow-y: ${viewAll ? 'scroll' : 'hidden'};
  `
  return (
    <DialogOverlay
      {...overlay}
      title={
        genericVersion
          ? t('edit-relationships.cross-record-warning-overlay.are-you-sure', 'Are you sure?')
          : t('edit-relationships.cross-record-warning-overlay.heading', 'Before Adding this Relationship')
      }
    >
      <Paragraph size="sm">
        {genericVersion
          ? t(
              'edit-relationships.cross-record-warning-overlay.content.generic-text',
              '{name} is in another record with existing relationships. To move {name}, old relationships will need to be removed. ',
              { name }
            )
          : t(
              'edit-relationships.cross-record-warning-overlay.content.text',
              '{name} is in another record with existing relationships. To create this relationship, old relationships will need to be removed. ',
              { name }
            )}
        {!genericVersion && (
          <InlineButton to="https://www.familysearch.org/en/help/helpcenter/article/how-do-i-add-a-name-to-a-relationship-in-historical-records-when-the-name-is-currently-connected-to-the-wrong-record">
            {t('edit-relationships.cross-record-warning-overlay.learn-more', 'Learn more')}
          </InlineButton>
        )}
      </Paragraph>
      <List>
        <ListItem
          primaryText={name}
          overline={person?.primary ? t('edit-relationships.cross-record-warning-overlay.principal', 'Principal') : ''}
          startElement={<ListItem.Icon Icon={DocumentBasic} />}
        />
      </List>
      <Paragraph size="sm">
        {t(
          'edit-relationships.cross-record-warning-overlay.view-relationships',
          'The following relationships will be removed:'
        )}
      </Paragraph>
      <div css={relationshipsListCss}>
        <List>
          {existingRelationships.map((relationship) => {
            const relationshipPerson = getNestedOrFloatingElementById(state.images, relationship.otherPersonId)
            return (
              <ListItem
                key={relationship.id}
                overline={`${
                  relationshipPerson?.primary
                    ? `${t('edit-relationships.cross-record-warning-overlay.principal', 'Principal')} | `
                    : ''
                }${relationship.label}`}
                primaryText={relationship.value}
                endElement={
                  <ListItem.Icon
                    Icon={MenuNewWindow}
                    onClick={() => {
                      openRelationshipArk(relationshipPerson, relationship, state.currentImageArk)
                    }}
                  />
                }
              />
            )
          })}
        </List>
      </div>
      {existingRelationships.length > 2 ? (
        <Button onClick={() => setViewAll(!viewAll)}>
          {t('edit-relationships.cross-record-warning-overlay.view-all', 'View All')}
        </Button>
      ) : null}
      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button
            onClick={() => {
              overlay.close()
              if (onCancel) {
                onCancel()
              }
            }}
          >
            {t('edit-relationships.cross-record-warning-overlay.cancel.button', 'Cancel')}
          </Button>
          <DangerButton
            onClick={() => {
              overlay.close()
              if (onContinue) {
                onContinue()
              }
            }}
            Icon={PersonRemove}
          >
            {t('edit-relationships.cross-record-warning-overlay.remove.button', 'Remove Relationships')}
          </DangerButton>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}
